import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

// Lets overlays (bottom sheets, pushed pages) hide the tab bar while they're
// open. MainTabs hosts the provider; overlays acquire a hold for their
// lifetime via useHideTabBar(). Counter-based so stacked overlays work.
const TabBarVisibilityContext = createContext<{
  hidden: boolean;
  acquire: () => () => void;
} | null>(null);

export const TabBarVisibilityProvider = ({
  children,
}: {
  children: (hidden: boolean) => ReactNode;
}) => {
  const [holds, setHolds] = useState(0);
  const acquire = useCallback(() => {
    setHolds((h) => h + 1);
    return () => setHolds((h) => h - 1);
  }, []);
  const value = useMemo(
    () => ({ hidden: holds > 0, acquire }),
    [holds, acquire],
  );

  return (
    <TabBarVisibilityContext.Provider value={value}>
      {children(holds > 0)}
    </TabBarVisibilityContext.Provider>
  );
};

// Hide the tab bar while the calling component is mounted. Safe without a
// provider (Storybook renders sheets standalone) — it just does nothing.
export const useHideTabBar = () => {
  const ctx = useContext(TabBarVisibilityContext);
  useEffect(() => {
    if (!ctx) return;
    return ctx.acquire();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx?.acquire]);
};
